import pdfMake from 'pdfmake/build/pdfmake'
import * as pdfFonts from 'pdfmake/build/vfs_fonts'
import type { Column, Content, TDocumentDefinitions } from 'pdfmake/interfaces'
import { logError } from '@/utils/errorUtils'
import { pdfBytesToBase64, urlToDataURL } from '../OrderTests/pdf/pdfHelpers'
import { BRAND_BLUE, TABLE_LINE_COLOR, TEXT_SECONDARY } from '../OrderTests/pdf/constants'
import logo from '/logo.png'

const vfs =
  (pdfFonts as Record<string, unknown>).vfs ||
  (pdfFonts as Record<string, Record<string, unknown>>).pdfMake?.vfs

if (vfs) {
  pdfMake.vfs = vfs as Record<string, string>
}

export type LetterPdfProvider = {
  organizationName: string
  /** Practitioner name including credentials, e.g. "Gregory House, MD". */
  providerName: string
  npi: string
  fax: string
  address: string
  city: string
  state: string
  zip: string
}

export type LetterPdfInput = {
  title: string
  /** The practitioner-reviewed letter body, rendered verbatim. */
  body: string
  patientName: string
  patientDateOfBirth: string
  provider: LetterPdfProvider
  /**
   * Data-URL of the practitioner's signature, as captured by SignatureModal.
   * Omitted for a letter whose policy needs no provider signature (an
   * administrative letter a coordinator sends) — the rule and printed name still
   * appear, just without a signature over them.
   */
  signatureDataUrl?: string
  signatureDate: string
  /**
   * Adds blank patient signature lines under the practitioner's. Set for documents
   * the patient has to sign themselves — the records authorization only carries
   * weight once the patient signs it.
   */
  patientSignature?: boolean
}

export type LetterPdfOutput = {
  bytes: Uint8Array
  base64: string
  fileName: string
}

function buildProviderHeaderStack(provider: LetterPdfProvider): Content {
  const stack: Content[] = []

  if (provider.organizationName) {
    stack.push({
      text: provider.organizationName,
      fontSize: 9,
      bold: true,
      color: BRAND_BLUE,
      margin: [0, 0, 0, 2],
    })
  }

  if (provider.providerName) {
    const npiPart = provider.npi ? `  |  NPI: ${provider.npi}` : ''
    stack.push({
      text: `${provider.providerName}${npiPart}`,
      fontSize: 7.5,
      color: '#333',
      margin: [0, 0, 0, 1],
    })
  }

  if (provider.fax) {
    stack.push({
      text: `Fax: ${provider.fax}`,
      fontSize: 7,
      bold: true,
      color: '#555',
      margin: [0, 0, 0, 1],
    })
  }

  const address = [provider.address, provider.city, provider.state, provider.zip]
    .filter(Boolean)
    .join(', ')
  if (address) {
    stack.push({ text: address, fontSize: 7, color: '#555' })
  }

  return { stack }
}

function buildHeader(logoDataUrl: string, provider: LetterPdfProvider): Content {
  return {
    columns: [
      logoDataUrl
        ? { image: logoDataUrl, width: 60, margin: [0, 0, 15, 0] }
        : { text: 'beHuman', fontSize: 11, bold: true, color: '#111827', margin: [0, 0, 16, 0] },
      {
        stack: [buildProviderHeaderStack(provider)],
        margin: [8, 0, 0, 0],
        width: '*',
      },
    ],
    margin: [0, 0, 0, 6],
  }
}

function buildSectionTitle(
  title: string,
  margin: [number, number, number, number] = [0, 6, 0, 2],
): Content {
  return {
    text: title.toUpperCase(),
    fontSize: 8,
    bold: true,
    color: BRAND_BLUE,
    margin,
  }
}

function buildPatientInformationTable(input: LetterPdfInput): Content {
  const rows: [string, string][] = [
    ['Name', input.patientName || '-'],
    ['Date of Birth', input.patientDateOfBirth],
  ]

  return {
    table: {
      widths: ['30%', '70%'],
      body: rows
        .filter(([, value]) => value)
        .map(([key, value]) => [
          { text: key, fontSize: 7, bold: true, color: TEXT_SECONDARY },
          { text: value, fontSize: 7 },
        ]),
    },
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0,
      hLineColor: () => TABLE_LINE_COLOR,
      paddingTop: () => 1,
      paddingBottom: () => 1,
    },
    margin: [0, 0, 0, 2],
  }
}

function buildBodyContent(body: string): Content[] {
  return body
    .split('\n')
    .map((line) => line.trimEnd())
    .map((line) => ({
      text: line || ' ',
      fontSize: 8,
      lineHeight: 1.25,
      margin: [0, 0, 0, line ? 3 : 1] as [number, number, number, number],
    }))
}

/** Height reserved above every signature rule, so the rules line up across columns. */
const SIGNATURE_VALUE_HEIGHT = 26

/**
 * One signature column: a fixed-height box for the signed value, the rule, then the
 * printed name and caption. The box height is fixed rather than left to the content
 * because `fit` scales a signature image by its aspect ratio — letting the image
 * drive the height is what pushed the date's rule below the signature's.
 */
function buildSignatureColumn(args: {
  width: string
  value?: Content
  ruleWidth: number
  name: string
  caption: string
}): Column {
  return {
    width: args.width,
    stack: [
      {
        table: { widths: ['*'], heights: [SIGNATURE_VALUE_HEIGHT], body: [[args.value ?? '']] },
        layout: {
          defaultBorder: false,
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0,
        },
      },
      {
        canvas: [
          {
            type: 'line',
            x1: 0,
            y1: 0,
            x2: args.ruleWidth,
            y2: 0,
            lineWidth: 0.5,
            lineColor: '#333',
          },
        ],
        margin: [0, 0, 0, 2],
      },
      // A blank keeps the caption on the same line as the other column's name.
      { text: args.name || ' ', fontSize: 8 },
      { text: args.caption, fontSize: 6, color: '#777', margin: [0, 1, 0, 0] },
    ],
  }
}

function buildSignatureBlock(input: LetterPdfInput): Content {
  return {
    columns: [
      buildSignatureColumn({
        width: '55%',
        value: input.signatureDataUrl
          ? { image: input.signatureDataUrl, fit: [150, SIGNATURE_VALUE_HEIGHT] }
          : undefined,
        ruleWidth: 200,
        name: input.provider.providerName,
        caption: input.signatureDataUrl ? 'Practitioner Signature' : 'Prepared by',
      }),
      buildSignatureColumn({
        width: '*',
        // Sits at the bottom of the box so the date reads as written on the rule.
        value: { text: input.signatureDate, fontSize: 8, margin: [0, 15, 0, 0] },
        ruleWidth: 130,
        name: '',
        caption: 'Date',
      }),
    ],
    margin: [0, 22, 0, 0],
  }
}

/**
 * Blank signature lines for a document the patient has to sign by hand (the records
 * authorization). Same geometry as the practitioner block so the page reads as one.
 */
function buildPatientSignatureBlock(patientName: string): Content {
  return {
    columns: [
      buildSignatureColumn({
        width: '55%',
        ruleWidth: 200,
        name: patientName,
        caption: 'Patient Signature',
      }),
      buildSignatureColumn({ width: '*', ruleWidth: 130, name: '', caption: 'Date' }),
    ],
    margin: [0, 18, 0, 0],
  }
}

function slugify(value: string): string {
  return value
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase()
}

function getFileName(input: LetterPdfInput): string {
  const parts = [slugify(input.title), slugify(input.patientName)].filter(Boolean)
  return `${parts.join('-') || 'patient-letter'}.pdf`
}

export async function generateLetterPdf(input: LetterPdfInput): Promise<LetterPdfOutput> {
  let logoDataUrl = ''
  try {
    logoDataUrl = await urlToDataURL(logo)
  } catch (err) {
    logError('Letters', 'Letter PDF logo fetch', err)
  }

  const generatedAt = new Date()
  const generatedDate = generatedAt.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  const content: Content[] = [
    buildHeader(logoDataUrl, input.provider),
    {
      text: input.title,
      fontSize: 13,
      bold: true,
      color: BRAND_BLUE,
      margin: [0, 4, 0, 1],
    },
    {
      text: `Generated on ${generatedDate}`,
      fontSize: 7,
      color: '#999',
      margin: [0, 0, 0, 4],
    },
    {
      canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, lineColor: BRAND_BLUE }],
      margin: [0, 0, 0, 4],
    },
    buildSectionTitle('Patient Information'),
    buildPatientInformationTable(input),
    buildSectionTitle('Letter', [0, 8, 0, 6]),
    ...buildBodyContent(input.body),
    buildSignatureBlock(input),
    ...(input.patientSignature ? [buildPatientSignatureBlock(input.patientName)] : []),
  ]

  const copyrightText = `Copyright © ${generatedAt.getFullYear()} beHuman Corp. The contents of this document are intended only for the use of the individual or entity to which they are addressed and may contain information that is legally privileged, confidential, and exempt from disclosure, including protected health information. If you are not the intended recipient, you are hereby notified that any dissemination, distribution, or copying of this document is strictly prohibited. If you have received or obtained this document in error, please notify the original sender or contact beHuman Corp. by email at privacy@behuman.health and delete this document immediately.`

  const docDefinition: TDocumentDefinitions = {
    pageSize: 'LETTER',
    pageMargins: [36, 30, 36, 65],
    content,
    footer: (currentPage, pageCount) => ({
      stack: [
        {
          columns: [
            {
              text: `${input.provider.organizationName || 'beHuman'} — ${input.title}`,
              fontSize: 6,
              color: '#999',
              margin: [36, 0, 0, 0],
            },
            {
              text: `Page ${currentPage} of ${pageCount}`,
              fontSize: 6,
              color: '#999',
              alignment: 'right',
              margin: [0, 0, 36, 0],
            },
          ],
        },
        {
          text: copyrightText,
          fontSize: 4.5,
          color: '#aaa',
          margin: [36, 3, 36, 0],
          lineHeight: 1.1,
        },
      ],
    }),
    defaultStyle: {
      fontSize: 8,
    },
  }

  const bytes = await new Promise<Uint8Array>((resolve, reject) => {
    const timeoutId = setTimeout(() => reject(new Error('PDF generation timed out')), 30_000)
    try {
      pdfMake.createPdf(docDefinition).getBuffer((buffer) => {
        clearTimeout(timeoutId)
        resolve(new Uint8Array(buffer))
      })
    } catch (err) {
      clearTimeout(timeoutId)
      reject(err instanceof Error ? err : new Error(String(err)))
    }
  })

  return {
    bytes,
    base64: pdfBytesToBase64(bytes),
    fileName: getFileName(input),
  }
}
